import React, { useEffect, useRef, useState } from "react";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import {
  formatEmployeeIdForApi,
  normalizeEmployeeIdDigits,
  parseEmployeeIdDigits,
  sanitizeEmployeeIdInput,
} from "../../utils/employeeId";
import downArrow from "../../assets/image/down-arrow.svg";
import PhoneNumberField from "../../Components/PhoneNumberField/PhoneNumberField";
import { FIXED_COUNTRY_CODE } from "../../utils/phone";

const selectBaseClass =
  "md:h-[54px] h-[44px] w-full appearance-none rounded-3 border-[1px] bg-transparent px-3 pr-10 text-[15px] outline-none disabled:cursor-not-allowed disabled:opacity-70";

const SelectChevron = ({ className = "" }) => (
  <img
    src={downArrow}
    alt=""
    aria-hidden
    className={`pointer-events-none absolute top-1/2 h-[7px] w-[7px] -translate-y-1/2 shrink-0 ${className}`}
  />
);

const FormSelect = ({ className = "", wrapperClassName = "", hasError = false, children, ...props }) => (
  <div className={`relative ${wrapperClassName}`}>
    <select
      className={`${selectBaseClass} border-[#0B3E64] ${hasError ? "border-red-500" : ""} ${className}`}
      {...props}
    >
      {children}
    </select>
    <SelectChevron className="right-3" />
  </div>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_IN_RE = /^[6-9]\d{9}$/;

const initialErrors = {
  employee_id: "",
  full_name: "",
  email: "",
  mobile_number: "",
  mipl_location_id: "",
  fuel_pump_id: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const mapFuelPumpOption = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.fuel_pump_name || item?.name || "Fuel pump",
});

const EditFuelOperator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubheading } = useOutletContext();
  const operator = location.state?.operator;

  const prefilledPumpIdsRef = useRef(null);

  const [miplLocations, setMiplLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [fuelPumps, setFuelPumps] = useState([]);
  const [loadingFuelPumps, setLoadingFuelPumps] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [fuelOperatorId, setFuelOperatorId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [miplLocationId, setMiplLocationId] = useState("");
  const [selectedFuelPumpIds, setSelectedFuelPumpIds] = useState([]);
  const [fuelPumpDropdownOpen, setFuelPumpDropdownOpen] = useState(false);
  const fuelPumpDropdownRef = useRef(null);

  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    setSubheading("Edit Fuel Operator");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    if (!operator?.id) return;
    prefilledPumpIdsRef.current =
      operator.fuelPumpIds?.length ? [...operator.fuelPumpIds] : null;
    setFuelOperatorId(operator.id);
    const eid = operator.employeeId;
    setEmployeeId(eid && eid !== "-" ? parseEmployeeIdDigits(eid) : "");
    const nm = operator.name;
    setFullName(nm && nm !== "-" ? nm : "");
    const em = operator.email;
    setEmail(em && em !== "-" ? em : "");
    setMobileNumber(operator.mobileDigits || "");
    setMiplLocationId(operator.miplLocationId || "");
  }, [operator]);

  const fetchMiplLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        const raw = Array.isArray(d) ? d : toArray(d?.location_data);
        const mapped = raw.map(mapLocation).filter((x) => x.id && x.name);
        setMiplLocations(mapped);
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load MIPL locations";
        notifyError(errMsg);
        setMiplLocations([]);
      }
    } catch {
      notifyError("Failed to load MIPL locations");
      setMiplLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    fetchMiplLocations();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFuelPumps = async () => {
      if (!miplLocationId) {
        setFuelPumps([]);
        setSelectedFuelPumpIds([]);
        setFuelPumpDropdownOpen(false);
        setLoadingFuelPumps(false);
        return;
      }

      setLoadingFuelPumps(true);
      const pendingPrefill = prefilledPumpIdsRef.current;
      if (!pendingPrefill) {
        setSelectedFuelPumpIds([]);
      }
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.listOfAllFuelPump}?mipl_location_id=${encodeURIComponent(miplLocationId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const raw =
            (Array.isArray(d) ? d : null) ||
            toArray(d?.fuel_pump_data) ||
            toArray(d?.fuel_pumps) ||
            [];
          const mapped = raw.map(mapFuelPumpOption).filter((x) => x.id);
          if (cancelled) return;
          setFuelPumps(mapped);
          if (pendingPrefill?.length) {
            const valid = pendingPrefill.filter((id) => mapped.some((m) => m.id === id));
            setSelectedFuelPumpIds(valid);
            prefilledPumpIdsRef.current = null;
          }
        } else {
          if (cancelled) return;
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel pumps";
          notifyError(errMsg);
          setFuelPumps([]);
          if (!pendingPrefill) setSelectedFuelPumpIds([]);
        }
      } catch {
        if (cancelled) return;
        notifyError("Failed to load fuel pumps");
        setFuelPumps([]);
        if (!pendingPrefill) setSelectedFuelPumpIds([]);
      } finally {
        if (!cancelled) setLoadingFuelPumps(false);
      }
    };

    loadFuelPumps();
    return () => {
      cancelled = true;
    };
  }, [miplLocationId]);

  useEffect(() => {
    setFuelPumpDropdownOpen(false);
  }, [miplLocationId]);

  useEffect(() => {
    if (!fuelPumpDropdownOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (fuelPumpDropdownRef.current && !fuelPumpDropdownRef.current.contains(e.target)) {
        setFuelPumpDropdownOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFuelPumpDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [fuelPumpDropdownOpen]);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const toggleFuelPump = (pumpId) => {
    setSelectedFuelPumpIds((prev) =>
      prev.includes(pumpId) ? prev.filter((id) => id !== pumpId) : [...prev, pumpId]
    );
    clearFieldError("fuel_pump_id");
  };

  const removeFuelPump = (pumpId) => {
    setSelectedFuelPumpIds((prev) => prev.filter((id) => id !== pumpId));
    clearFieldError("fuel_pump_id");
  };

  const validate = () => {
    const next = { ...initialErrors };
    const id = normalizeEmployeeIdDigits(employeeId);
    const name = (fullName || "").trim();
    const em = (email || "").trim();
    const mobile = (mobileNumber || "").replace(/\D/g, "");
    const loc = (miplLocationId || "").trim();

    if (!id) next.employee_id = "Employee ID is required";

    if (!name) next.full_name = "Name is required";
    else if (name.length < 2) next.full_name = "Name must be at least 2 characters";
    else if (name.length > 100) next.full_name = "Name is too long";
    else if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(name)) {
      next.full_name = "Enter a valid name";
    }

    if (!em) next.email = "Email is required";
    else if (!EMAIL_RE.test(em)) next.email = "Enter a valid email address";

    if (!mobile) next.mobile_number = "Phone number is required";
    else if (!MOBILE_IN_RE.test(mobile)) {
      next.mobile_number = "Enter a valid 10-digit Indian mobile number";
    }

    if (!loc) next.mipl_location_id = "Select a MIPL location";
    else if (!selectedFuelPumpIds.length) next.fuel_pump_id = "Select at least one fuel pump";

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!fuelOperatorId) {
      notifyError("Missing fuel operator id");
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        fuel_operator_id: fuelOperatorId,
        employee_id: formatEmployeeIdForApi(normalizeEmployeeIdDigits(employeeId)),
        full_name: (fullName || "").trim(),
        email: (email || "").trim().toLowerCase(),
        mobile_number: (mobileNumber || "").replace(/\D/g, ""),
        country_code: FIXED_COUNTRY_CODE,
        mipl_location_id: (miplLocationId || "").trim(),
        fuel_pump_id: [...selectedFuelPumpIds],
      };

      const response = await handleApiCall("put", endpoint.editFuelOperator, payload);
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Fuel operator updated successfully");
        setTimeout(() => {
          navigate("/fuel-operator-management");
        }, 1500);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to update fuel operator";
      notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const disabledForm = submitting || loadingLocations;
  const fuelPumpTriggerDisabled =
    disabledForm ||
    loadingFuelPumps ||
    !miplLocationId ||
    fuelPumps.length === 0;

  const fuelPumpTriggerText = () => {
    if (!miplLocationId) return "Select MIPL location first";
    if (loadingFuelPumps) return "Loading fuel pumps…";
    if (fuelPumps.length === 0) return "No fuel pumps available";
    const count = selectedFuelPumpIds.length;
    if (count === 0) return "Select fuel pump(s)";
    if (count === 1) return "1 fuel pump selected";
    return `${count} fuel pumps selected`;
  };

  const selectedFuelPumpCount = miplLocationId ? selectedFuelPumpIds.length : 0;

  if (!operator?.id) {
    return (
      <>
        <div className="row col-md-12 bg-white mx-0 mt-6 p-4 shadow-sm font-UbuntuRegular">
          <p className="mb-3 text-gray-600">
            No fuel operator selected. Use <strong>Edit</strong> from the fuel operator list.
          </p>
          <Link to="/fuel-operator-management" className="btn btn-primary">
            Back to list
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
    <Toastify/>
      <form
        onSubmit={onSubmit}
        className="row col-md-12 bg-white mx-0 mt-6 px-2 py-3 md:p-4 shadow-sm font-UbuntuRegular g-3"
        noValidate
      >
        <div className="col-lg-3 col-md-6 mb-0 min-w-0 mt-0 md:!mt-[1rem]">
          <label className="font-UbuntuMedium">Employee ID</label>
          <div
            className={`mt-2 flex w-full min-w-0 items-stretch overflow-hidden rounded-3 border-[1px] ${
              errors.employee_id ? "border-red-500" : "border-[#0B3E64]"
            }`}
          >
            <span
              className="flex shrink-0 items-center border-r border-[#0B3E64]/25 bg-[#f5f8fa] px-3 font-UbuntuMedium text-[#0B3E64]"
              aria-hidden
            >
              MI
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={5}
              placeholder="00000"
              className="min-w-0 flex-1 border-0 bg-transparent px-3 py-0 outline-none md:h-[54px] h-[44px]"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(sanitizeEmployeeIdInput(e.target.value));
                clearFieldError("employee_id");
              }}
              onBlur={() => {
                const normalized = normalizeEmployeeIdDigits(employeeId);
                if (normalized !== employeeId) setEmployeeId(normalized);
              }}
              disabled={disabledForm}
              aria-label="Employee ID number (5 digits)"
            />
          </div>
          {errors.employee_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.employee_id}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Fuel Operator Name</label>
          <input
            type="text"
            className={`w-full h-[44px] md:h-[54px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
              errors.full_name ? "border-red-500" : ""
            }`}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              clearFieldError("full_name");
            }}
            autoComplete="name"
            disabled={disabledForm}
          />
          {errors.full_name ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.full_name}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Email</label>
          <input
            type="email"
            className={`w-full h-[44px] md:h-[54px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
              errors.email ? "border-red-500" : ""
            }`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            autoComplete="email"
            disabled={disabledForm}
          />
          {errors.email ? <p className="text-red-500 text-sm mt-1 mb-0">{errors.email}</p> : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Phone Number</label>
          <div className="mt-2">
            <PhoneNumberField
              value={mobileNumber}
              onChange={(v) => {
                setMobileNumber(v);
                clearFieldError("mobile_number");
              }}
              disabled={disabledForm}
              hasError={!!errors.mobile_number}
            />
          </div>
          {errors.mobile_number ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.mobile_number}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Select MIPL Location</label>
          <FormSelect
            wrapperClassName="mt-2 max-w-full"
            hasError={!!errors.mipl_location_id}
            value={miplLocationId}
            onChange={(e) => {
              prefilledPumpIdsRef.current = null;
              setMiplLocationId(e.target.value);
              setSelectedFuelPumpIds([]);
              setFuelPumps([]);
              setFuelPumpDropdownOpen(false);
              setErrors((prev) => ({
                ...prev,
                mipl_location_id: "",
                fuel_pump_id: "",
              }));
            }}
            disabled={disabledForm || loadingLocations}
          >
            <option value="">{loadingLocations ? "Loading locations…" : "Select location"}</option>
            {miplLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </FormSelect>
          {errors.mipl_location_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.mipl_location_id}</p>
          ) : null}
        </div>
        <div className="col-lg-6 col-md-12 mb-0 min-w-0" ref={fuelPumpDropdownRef}>
          <label className="font-UbuntuMedium" htmlFor="fuel-pump-multi-trigger-edit">
            Select fuel pump(s)
          </label>
          <div className="relative mt-2">
            <button
              id="fuel-pump-multi-trigger-edit"
              type="button"
              aria-haspopup="listbox"
              aria-expanded={fuelPumpDropdownOpen}
              disabled={fuelPumpTriggerDisabled}
              onClick={() => {
                if (fuelPumpTriggerDisabled) return;
                setFuelPumpDropdownOpen((o) => !o);
              }}
              className={`flex h-[44px] md:h-[54px] w-full items-center justify-between gap-2 rounded-3 border-[1px] bg-transparent px-3 text-left outline-none ${
                errors.fuel_pump_id ? "border-red-500" : "border-[#0B3E64]"
              } ${fuelPumpTriggerDisabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
            >
              <span className="truncate text-[15px]">{fuelPumpTriggerText()}</span>
              <img
                src={downArrow}
                alt=""
                aria-hidden
                className={`h-[7px] w-[7px] shrink-0 transition-transform duration-150 ${
                  fuelPumpDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {fuelPumpDropdownOpen && !fuelPumpTriggerDisabled ? (
              <div
                role="listbox"
                aria-multiselectable="true"
                className="absolute left-0 right-0 top-[calc(100%+4px)] z-[100] max-h-[240px] overflow-y-auto rounded-3 border border-[#0B3E64] bg-white py-1 shadow-lg"
              >
                {fuelPumps.map((fp) => {
                  const selected = selectedFuelPumpIds.includes(fp.id);
                  return (
                    <button
                      key={fp.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleFuelPump(fp.id)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-[15px] transition-colors ${
                        selected
                          ? "bg-[#0B3E64]/10 font-UbuntuMedium text-[#0B3E64]"
                          : "bg-white hover:bg-[#0B3E64]/[0.06]"
                      }`}
                    >
                      <span
                        className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded border-2 text-[11px] leading-none ${
                          selected
                            ? "border-[#0B3E64] bg-[#0B3E64] text-white"
                            : "border-[#0B3E64]/35 bg-white text-transparent"
                        }`}
                        aria-hidden
                      >
                        ✓
                      </span>
                      <span className="flex-1 truncate">{fp.name}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          {errors.fuel_pump_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.fuel_pump_id}</p>
          ) : null}
          {selectedFuelPumpCount > 0 ? (
            <>
              <p className="mb-1 mt-3 text-sm text-gray-600">
                Selected fuel pumps ({selectedFuelPumpCount})
              </p>
              <div className="flex flex-wrap gap-2">
                {miplLocationId && selectedFuelPumpIds.map((id) => {
                  const fp = fuelPumps.find((p) => p.id === id);
                  const label = fp?.name || id;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-200 pl-3 pr-1 py-1 text-sm font-UbuntuRegular"
                    >
                      <span className="max-w-[200px] truncate" title={label}>
                        {label}
                      </span>
                      <button
                        type="button"
                        className="flex h-7 min-w-[28px] shrink-0 items-center justify-center rounded-full text-lg leading-none text-[#0B3E64] hover:bg-black/10"
                        disabled={disabledForm}
                        aria-label={`Remove ${label}`}
                        onClick={() => removeFuelPump(id)}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
        <div className="col-md-12 mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 pt-2">
          <Link
            to="/fuel-operator-management"
            className="btn btn-primary"
            onClick={(e) => submitting && e.preventDefault()}
          >
            Cancel
          </Link>
          <button type="submit" className="btn btn-danger md:min-w-32" disabled={disabledForm}>
            {submitting ? "Updating…" : "Update"}
          </button>
        </div>
      </form>
    </>
  );
};

export default EditFuelOperator;
